import {
  Button,
  Container,
  Flex,
  Image,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react"
import { Link } from "@tanstack/react-router"
import { motion, useScroll, useTransform } from "framer-motion"

import landingBg from "#/assets/gradient-bg.png"
import mobileBg from "#/assets/hand-mobile.png"
import mobileLogo from "#/assets/landing-bank-mobile-title.svg"
import landingLogo from "#/assets/landing-bank-title.svg"
import { LANDING_CONTAINER_WIDTH } from "#/shared/constants"

type HeroSectionProps = {
  subheader?: string
  subheader2?: string
  heroCTAText?: string
  heroCTALink?: string
  heroCTABgColor?: string
  heroCTAFontColor?: string
  heroImageUrl?: string
}

export default function HomeHero({
  subheader = "",
  subheader2 = "",
  heroCTAText = "",
  heroCTALink = "",
  heroCTABgColor = "",
  heroCTAFontColor = "",
  heroImageUrl = "",
}: HeroSectionProps) {
  const { scrollY } = useScroll()
  const bgY = useTransform(scrollY, [0, 300], ["20px", "100px"])
  const bgPosition = useTransform(bgY, (y) => `50% ${y}`)

  const backgroundImageUrl = useBreakpointValue({
    base: mobileBg,
    md: heroImageUrl,
  })

  return (
    <>
      <style>{`
      .hero-title i {
        margin-left: -5px;
        font-family: var(--chakra-fonts-gtalpina-thinitalic);
        font-style: normal!important;
      }

      @media screen and (min-width: 2080px) {
        .hero-title {
          font-size: 250px
        }
        .hero-bg {
          background-size: cover!important;
        }
      }

      @media screen and (max-width: 1080px) {
        .hero-bg {
          background-size: cover!important;
          background-position: 80% 100px!important;
        }

      @media screen and (max-width: 780px) {
        .hero-bg {
          background-size: 100%!important;
          background-position: 80% 100px!important;
        }
        .hero-title i {
          margin-left: 0px;
        }
      }

  `}</style>
      <Container
        bgImage={landingBg}
        backgroundPosition="center right"
        backgroundSize="cover"
        height="100%"
        width="100%"
        maxWidth="100%"
        padding="0"
        mt="-90px"
      >
        <motion.div
          className="hero-bg"
          style={{
            backgroundImage: `url(${backgroundImageUrl})`,
            backgroundBlendMode: "darken",
            backgroundRepeat: "no-repeat",
            backgroundPosition: bgPosition,
            backgroundSize: "100%",
            height: "120vh",
            width: "100%",
            maxWidth: LANDING_CONTAINER_WIDTH,
            margin: "auto",
          }}
        >
          <Container
            maxW="100%"
            p={0}
            height="100vh"
            mt="25px"
            pt="100px"
            position="relative"
          >
            <Flex
              textAlign={{ base: "left", md: "center" }}
              flexDirection="column"
              justifyContent="center"
              maxWidth={{ base: "90%", md: "100%" }}
              margin="auto"
              height={{ base: "100vh", sm: "100vh", md: "85vh", xl: "100vh" }}
              marginTop={{ base: "40px", sm: "-50px", md: 0 }}
            >
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 100,
                  damping: 15,
                  delay: 0.8,
                }}
              >
                <Image
                  src={landingLogo}
                  margin="auto"
                  display={{ base: "none", md: "block" }}
                />
                <Image
                  src={mobileLogo}
                  margin="0"
                  w="100%"
                  display={{ base: "block", md: "none" }}
                />
              </motion.div>
              <motion.div
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 100,
                  damping: 15,
                  delay: 1.2,
                }}
              >
                <Flex
                  margin="20px auto"
                  justifyContent="space-around"
                  alignItems={{ base: "start", md: "center" }}
                  flexDirection={{ base: "column", md: "column" }}
                >
                  <Text
                    fontSize={{ base: "26px", sm: "26px", md: "2em" }}
                    maxWidth={{ base: "100%", md: "70%" }}
                    lineHeight="120%"
                    margin="auto"
                    color="primary.black.100"
                    textAlign={{ base: "center", md: "center" }}
                  >
                    {subheader || "Bank on yourself"}
                    {subheader2}
                  </Text>
                  <Text
                    fontSize={{ base: "1.25em", sm: "1.25em", md: "2em" }}
                    maxWidth={{ base: "100%", md: "50%" }}
                    display={{ base: "block", md: "none" }}
                    m="20px auto"
                    marginBottom={{ base: 0, sm: "100px", md: 0 }}
                    lineHeight="120%"
                    textAlign="center"
                    bgGradient="linear-gradient(90deg, #FF004D 15.77%, #990070 104.02%)"
                    bgClip="text"
                    color="transparent"
                  >
                    (Experience it best on desktop)
                  </Text>
                  {heroCTAText && heroCTALink && (
                    <Link to={heroCTALink}>
                      <Button
                        display={{ base: "none", md: "block" }}
                        bg={heroCTABgColor || "primary.black.100"}
                        color={heroCTAFontColor || "white"}
                        size="lg"
                        _hover={{
                          bg: "primary.red.100",
                          color: "primary.white.100",
                        }}
                        mt="40px"
                        fontFamily="riforma.medium"
                        fontWeight="500"
                        variant="primary"
                        p={{ base: "10px 20px", md: "10px 35px" }}
                        h="60px"
                        fontSize={{ base: "18px", md: "20px" }}
                        borderRadius="20px"
                        className="get_started-signup"
                      >
                        {heroCTAText || "Get Started"}
                      </Button>
                    </Link>
                  )}
                </Flex>
              </motion.div>
            </Flex>
          </Container>
        </motion.div>
      </Container>
    </>
  )
}
